/**
 * Help Center feature flag.
 *
 * HELP_CENTER_ENABLED is the launch kill-switch for the ENTIRE in-app Help
 * Center. It gates the `/help` route (src/App.tsx), the "Help Center" entry in
 * the sidebar avatar/profile menu (src/components/layout/Sidebar.tsx), and
 * therefore every path a reader has to open it (the `faq-open-help` article
 * points at exactly that avatar-menu entry). When off, the feature is
 * unreachable — no route, no menu item, no dead links. When on, it is
 * byte-identical to today.
 *
 * It is a build-time env flag (same mechanism as src/features/doordrop/flags.ts),
 * but with the OPPOSITE default: the Help Center is complete and verified, so it
 * DEFAULTS ON and the flag exists only to pull it at launch if needed.
 *
 *   VITE_ENABLE_HELP_CENTER  — present-and-not-'false' = ON (the default).
 *                              Set it to the string 'false' to turn the whole
 *                              Help Center off; unset, 'true', or any other
 *                              value leaves it on.
 *
 * Vite inlines env vars at build time, so flipping this requires an env change
 * plus a redeploy.
 *
 * RUNTIME COMPOSITION: this build-time flag is now the DEFAULT beneath a runtime
 * super-admin toggle. src/features/featureToggles resolves
 * `runtimeToggle ?? HELP_CENTER_ENABLED`, so a super-admin can flip the Help
 * Center off for every customer with no redeploy (system_settings key
 * `feature_help_center_enabled`). This const stays the fallback used while the
 * runtime value loads, or when it is absent/unreadable — so nothing flickers off.
 * The gate sites consume the resolved value via useFeatureToggle('help_center').
 *
 * RELATIONSHIP TO src/features/help/availability.ts: that file gates INDIVIDUAL
 * ARTICLES *within* a live Help Center (which guides are truthful for this
 * build). This flag gates the Help Center as a WHOLE. They are independent
 * concerns and compose: when this flag is off the Help Center cannot be opened
 * at all, so the per-article availability gates never come into play; when it is
 * on, availability.ts decides which articles show inside it.
 */
export const HELP_CENTER_ENABLED = import.meta.env.VITE_ENABLE_HELP_CENTER !== 'false';
